import { useState, useRef, useEffect } from 'react';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, BODY, MONO } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Dp, Ic } from './shared';
import { getCurrentOrigin, navigateTo } from '../lib/navigation';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_AUTH_URL = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(getCurrentOrigin() + '/auth/callback')}`;

const inputStyle = { width: '100%', padding: '14px 16px', marginBottom: 8, background: 'var(--faint)', border: `1px solid ${GHOST}`, color: 'var(--light)', fontFamily: BODY, fontSize: 14, outline: 'none', boxSizing: 'border-box', borderRadius: 8 };

export function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState('welcome');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const otpRefs = useRef([]);

  const handleEmailAuth = async (isSignup) => {
    setError('');
    if (!email || !password || (isSignup && !name)) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const endpoint = isSignup ? '/auth/signup' : '/auth/login';
      const body = isSignup ? { email, password, name } : { email, password };
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include'
      });
      if (!res.ok) {
        if (res.status === 401) throw new Error('Invalid email or password');
        if (res.status === 400) throw new Error('Email already registered');
        throw new Error('Authentication failed');
      }
      const user = await res.json();
      onAuth(user);
    } catch (e) {
      let errorMsg = e.message;
      if (errorMsg.includes('body stream') || errorMsg.includes('Response')) {
        errorMsg = 'Authentication failed. Please try again.';
      }
      setError(errorMsg);
    }
    setLoading(false);
  };

  const handleGoogleAuth = () => {
    navigateTo(GOOGLE_AUTH_URL);
  };

  const handleForgotPassword = async () => {
    setError('');
    if (!email) { setError('Enter your email address'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send code');
      }
      setSuccess('Code sent! Check your email.');
      setMode('otp');
    } catch (e) {
      let errorMsg = e.message;
      if (errorMsg.includes('body stream') || errorMsg.includes('Response')) {
        errorMsg = 'Failed to send code. Please try again.';
      }
      setError(errorMsg);
    }
    setLoading(false);
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    setError(''); setSuccess('');
    const code = otp.join('');
    if (code.length !== 6) { setError('Enter the full 6-digit code'); return; }
    if (loading) return; // Prevent double submission
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: code })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Invalid code');
      }
      setSuccess('Code verified!');
      setMode('reset');
    } catch (e) {
      let errorMsg = e.message;
      if (errorMsg.includes('body stream') || errorMsg.includes('Response')) {
        errorMsg = 'Invalid code. Please try again.';
      }
      setError(errorMsg);
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    setError(''); setSuccess('');
    if (!newPassword) { setError('Enter a new password'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords don\'t match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: otp.join(''), new_password: newPassword })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Reset failed');
      }
      setSuccess('Password reset! You can now log in.');
      setPassword('');
      setOtp(['', '', '', '', '', '']);
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => { setMode('login'); setSuccess(''); }, 1500);
    } catch (e) {
      let errorMsg = e.message;
      if (errorMsg.includes('body stream') || errorMsg.includes('Response')) {
        errorMsg = 'Reset failed. Please try again.';
      }
      setError(errorMsg);
    }
    setLoading(false);
  };

  // Auto-submit OTP when all 6 digits filled
  useEffect(() => {
    if (mode === 'otp' && otp.every(d => d !== '')) handleVerifyOtp();
  }, [otp, mode]);

  const ErrorBox = () => error ? (
    <div data-testid="auth-error" style={{ padding: '10px 14px', marginBottom: 12, background: `${EMBER}12`, border: `1px solid ${EMBER}25`, borderRadius: 8 }}>
      <Mo style={{ fontSize: 11, color: 'var(--ember)' }}>{error}</Mo>
    </div>
  ) : null;

  const SuccessBox = () => success ? (
    <div data-testid="auth-success" style={{ padding: '10px 14px', marginBottom: 12, background: `${GOLD}12`, border: `1px solid ${GOLD}25`, borderRadius: 8 }}>
      <Mo style={{ fontSize: 11, color: 'var(--gold)' }}>{success}</Mo>
    </div>
  ) : null;

  const BackBtn = ({ to }) => (
    <button onClick={() => { setMode(to); setError(''); setSuccess(''); }} data-testid="auth-back-btn" style={{
      background: 'var(--faint)', border: 'none', color: 'var(--mute)', fontFamily: MONO, fontSize: 10,
      cursor: 'pointer', marginBottom: 20, padding: '10px 16px', letterSpacing: 0.5, borderRadius: 8
    }}>&#8592; BACK</button>
  );

  // ── Welcome ──
  if (mode === 'welcome') return (
    <div data-testid="auth-welcome" style={{ padding: '60px 28px', textAlign: 'center', position: 'relative' }}>
      <Horizon intensity={0.8} />
      <div className="dettio-screen__inner">
        <div style={{ marginBottom: 24 }}><span className="str-b">{Ic.flame(48, GOLD)}</span></div>
        <Dp style={{ fontSize: 32, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -1, marginBottom: 8 }}>Dettio</Dp>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', lineHeight: 1.7, display: 'block', marginBottom: 40 }}>
          Math-based debt destruction.<br />No fluff. Just freedom.
        </Bd>
        <button onClick={handleGoogleAuth} data-testid="google-auth-btn" style={{
          width: '100%', padding: '14px', background: LIGHT, border: 'none', color: 'var(--base)',
          fontFamily: BODY, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 12, borderRadius: 8
        }}>
          <svg width={18} height={18} viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Continue with Google
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '16px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--faint)' }} />
          <Mo style={{ fontSize: 9, color: 'var(--ghost)', letterSpacing: 1 }}>OR</Mo>
          <div style={{ flex: 1, height: 1, background: 'var(--faint)' }} />
        </div>
        <Btn onClick={() => setMode('signup')} gold data-testid="signup-btn">CREATE ACCOUNT</Btn>
        <button onClick={() => setMode('login')} data-testid="login-btn" style={{
          width: '100%', marginTop: 8, padding: '14px', background: 'transparent',
          border: `1px solid ${GHOST}`, color: 'var(--light)', fontFamily: MONO, fontSize: 11,
          fontWeight: 600, cursor: 'pointer', letterSpacing: 0.5, borderRadius: 8
        }}>I HAVE AN ACCOUNT</button>
        
        {/* Privacy Policy Link - Required for Apple App Store */}
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <a 
            href="https://dettio.com/privacy" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              fontFamily: MONO, 
              fontSize: 9, 
              color: MUTE, 
              textDecoration: 'none',
              letterSpacing: 0.5
            }}
            data-testid="privacy-link"
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );

  // ── Forgot Password: Enter Email ──
  if (mode === 'forgot') return (
    <div data-testid="auth-forgot" style={{ padding: '40px 28px', position: 'relative' }}>
      <Horizon intensity={0.6} />
      <div className="dettio-screen__inner">
        <BackBtn to="login" />
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -0.5, marginBottom: 6 }}>Reset password</Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24 }}>
          Enter your email and we'll send a 6-digit code
        </Bd>
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" data-testid="forgot-email-input"
          onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
          style={inputStyle} />
        <ErrorBox />
        <SuccessBox />
        <Btn onClick={handleForgotPassword} gold disabled={loading} data-testid="forgot-submit-btn">
          {loading ? 'SENDING...' : 'SEND RESET CODE'}
        </Btn>
      </div>
    </div>
  );

  // ── OTP Entry ──
  if (mode === 'otp') return (
    <div data-testid="auth-otp" style={{ padding: '40px 28px', position: 'relative' }}>
      <Horizon intensity={0.6} />
      <div className="dettio-screen__inner">
        <BackBtn to="forgot" />
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -0.5, marginBottom: 6 }}>Enter code</Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 8 }}>
          We sent a 6-digit code to
        </Bd>
        <Bd style={{ fontSize: 13, color: 'var(--gold)', display: 'block', marginBottom: 24, fontWeight: 600 }}>{email}</Bd>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }} onPaste={handleOtpPaste}>
          {otp.map((digit, i) => (
            <input key={i} ref={el => otpRefs.current[i] = el} value={digit}
              onChange={e => handleOtpChange(i, e.target.value)}
              onKeyDown={e => handleOtpKeyDown(i, e)}
              data-testid={`otp-input-${i}`}
              type="text" inputMode="numeric" maxLength={1}
              style={{
                width: 46, height: 56, textAlign: 'center', fontSize: 22, fontWeight: 700,
                fontFamily: MONO, background: 'var(--faint)', border: `1px solid ${digit ? GOLD : GHOST}`,
                color: 'var(--light)', outline: 'none', borderRadius: 8,
                transition: 'border-color 0.2s'
              }}
            />
          ))}
        </div>
        <ErrorBox />
        <SuccessBox />
        <Btn onClick={handleVerifyOtp} gold disabled={loading} data-testid="otp-submit-btn">
          {loading ? 'VERIFYING...' : 'VERIFY CODE'}
        </Btn>
        <button onClick={() => { setOtp(['', '', '', '', '', '']); handleForgotPassword(); }} data-testid="resend-otp-btn" style={{
          width: '100%', marginTop: 12, padding: '10px', background: 'transparent', border: 'none',
          color: 'var(--mute)', fontFamily: BODY, fontSize: 12, cursor: 'pointer'
        }}>
          Didn't get it? <span style={{ color: 'var(--gold)', fontWeight: 600 }}>Resend code</span>
        </button>
      </div>
    </div>
  );

  // ── New Password ──
  if (mode === 'reset') return (
    <div data-testid="auth-reset" style={{ padding: '40px 28px', position: 'relative' }}>
      <Horizon intensity={0.6} />
      <div className="dettio-screen__inner">
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: `${GOLD}18`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={'var(--gold)'} strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          </div>
        </div>
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -0.5, marginBottom: 6, textAlign: 'center' }}>New password</Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24, textAlign: 'center' }}>
          Choose a strong password for your account
        </Bd>
        <input value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password" type="password" data-testid="reset-password-input"
          style={inputStyle} />
        <input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm password" type="password" data-testid="reset-confirm-input"
          onKeyDown={e => e.key === 'Enter' && handleResetPassword()}
          style={inputStyle} />
        <ErrorBox />
        <SuccessBox />
        <Btn onClick={handleResetPassword} gold disabled={loading} data-testid="reset-submit-btn">
          {loading ? 'RESETTING...' : 'SET NEW PASSWORD'}
        </Btn>
      </div>
    </div>
  );

  // ── Login / Signup Form ──
  return (
    <div data-testid={mode === 'login' ? 'auth-login' : 'auth-signup'} style={{ padding: '40px 28px', position: 'relative' }}>
      <Horizon intensity={0.6} />
      <div className="dettio-screen__inner">
        <BackBtn to="welcome" />
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -0.5, marginBottom: 6 }}>
          {mode === 'login' ? 'Welcome back' : 'Create account'}
        </Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24 }}>
          {mode === 'login' ? 'Pick up where you left off' : 'Start your debt freedom journey'}
        </Bd>
        <button onClick={handleGoogleAuth} data-testid="google-auth-btn-form" style={{
          width: '100%', padding: '14px', background: LIGHT, border: 'none', color: 'var(--base)',
          fontFamily: BODY, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 16, borderRadius: 8
        }}>
          <svg width={18} height={18} viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Continue with Google
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '12px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--faint)' }} />
          <Mo style={{ fontSize: 9, color: 'var(--ghost)', letterSpacing: 1 }}>OR</Mo>
          <div style={{ flex: 1, height: 1, background: 'var(--faint)' }} />
        </div>
        {mode === 'signup' && (
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" data-testid="auth-name-input" style={inputStyle} />
        )}
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" data-testid="auth-email-input" style={inputStyle} />
        <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" type="password" data-testid="auth-password-input"
          onKeyDown={e => e.key === 'Enter' && handleEmailAuth(mode === 'signup')}
          style={{ ...inputStyle, marginBottom: mode === 'login' ? 8 : 16 }} />

        {mode === 'login' && (
          <button onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }} data-testid="forgot-password-link" style={{
            background: 'none', border: 'none', color: 'var(--mute)', fontFamily: BODY, fontSize: 12,
            cursor: 'pointer', padding: '0 0 16px 0', display: 'block', textAlign: 'right', width: '100%'
          }}>
            Forgot password? <span style={{ color: 'var(--gold)' }}>Reset</span>
          </button>
        )}

        <ErrorBox />
        <Btn onClick={() => handleEmailAuth(mode === 'signup')} gold disabled={loading} data-testid="auth-submit-btn">
          {loading ? 'LOADING...' : mode === 'login' ? 'LOG IN' : 'CREATE ACCOUNT'}
        </Btn>
        <button onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); }} data-testid="auth-toggle-btn" style={{
          width: '100%', marginTop: 12, padding: '10px', background: 'transparent', border: 'none',
          color: 'var(--mute)', fontFamily: BODY, fontSize: 12, cursor: 'pointer'
        }}>
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{mode === 'login' ? 'Sign up' : 'Log in'}</span>
        </button>
      </div>
    </div>
  );
}
